import React, { useState } from 'react';
import { employeesAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'react-hot-toast';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

const DebugAPI = () => {
  const { token, isAuthenticated, employee } = useAuth();
  const [testResults, setTestResults] = useState('');
  const [loading, setLoading] = useState(false);

  const addLog = (message) => {
    setTestResults(prev => prev + '\n' + new Date().toISOString() + ': ' + message);
  };

  const testAuthentication = async () => {
    setLoading(true);
    setTestResults('');
    
    try {
      addLog('=== AUTHENTICATION TEST ===');
      addLog(`Token exists: ${!!token}`);
      addLog(`Is authenticated: ${isAuthenticated}`);
      addLog(`Employee: ${employee?.name || 'No employee'}`);
      addLog(`Token in localStorage: ${!!localStorage.getItem('token')}`);
      
      if (token) {
        addLog(`Token preview: ${token.substring(0, 20)}...`);
      }
      
    } catch (error) {
      addLog(`Authentication test error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const testEmployeesFetch = async () => {
    setLoading(true);
    setTestResults('');
    
    try {
      addLog('=== EMPLOYEES FETCH TEST ===');
      addLog('Making API call to fetch employees...');
      
      const response = await employeesAPI.getAll();
      addLog(`Success! Status: ${response.status}`);
      addLog(`Data: ${JSON.stringify(response.data, null, 2)}`);
      
    } catch (error) {
      addLog(`Fetch error: ${error.message}`);
      addLog(`Status: ${error.response?.status}`);
      addLog(`Data: ${JSON.stringify(error.response?.data, null, 2)}`);
    } finally {
      setLoading(false);
    }
  };

  const testEmployeeUpdate = async () => {
    setLoading(true);
    setTestResults('');
    
    try {
      addLog('=== EMPLOYEE UPDATE TEST ===');
      
      // First, fetch an employee to get a valid UUID
      addLog('Fetching employees to get a valid UUID...');
      const employeesResponse = await employeesAPI.getAll();
      
      if (employeesResponse.data?.data?.length > 0) {
        const testEmployee = employeesResponse.data.data[0];
        addLog(`Using employee: ${testEmployee.name} (${testEmployee.uuid})`);
        
        // Test update with minimal data
        const updateData = {
          name: testEmployee.name,
          email: testEmployee.email,
          phone: testEmployee.phone || '',
          role: testEmployee.role,
          is_active: testEmployee.is_active
        };
        
        addLog(`Update data: ${JSON.stringify(updateData, null, 2)}`);
        addLog('Making update API call...');
        
        const updateResponse = await employeesAPI.update(testEmployee.uuid, updateData);
        addLog(`Update Success! Status: ${updateResponse.status}`);
        addLog(`Response: ${JSON.stringify(updateResponse.data, null, 2)}`);
        
      } else {
        addLog('No employees found to test update');
      }
      
    } catch (error) {
      addLog(`Update error: ${error.message}`);
      addLog(`Status: ${error.response?.status}`);
      addLog(`Data: ${JSON.stringify(error.response?.data, null, 2)}`);
      addLog(`Headers: ${JSON.stringify(error.response?.headers, null, 2)}`);
    } finally {
      setLoading(false);
    }
  };

  const testBackendConnection = async () => {
    setLoading(true);
    setTestResults('');
    
    try {
      addLog('=== BACKEND CONNECTION TEST ===');
      addLog(`Testing connection to: ${API_BASE_URL}`);
      
      // Try a simple GET request to test connectivity
      const response = await fetch(`${API_BASE_URL.replace('/api', '')}/health`);
      addLog(`Health check status: ${response.status}`);
      
      if (response.ok) {
        const data = await response.text();
        addLog(`Health check response: ${data}`);
      } else {
        addLog('Health check failed');
      }
      
    } catch (error) {
      addLog(`Connection error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const showLastError = () => {
    setTestResults('');
    addLog('=== LAST EMPLOYEE UPDATE ERROR ===');
    
    const lastError = sessionStorage.getItem('lastEmployeeUpdateError');
    if (lastError) {
      try {
        const errorData = JSON.parse(lastError);
        addLog(`Timestamp: ${errorData.timestamp}`);
        addLog(`Error Message: ${errorData.message}`);
        addLog(`Status: ${errorData.status}`);
        addLog(`URL: ${errorData.url}`);
        addLog(`Method: ${errorData.method}`);
        addLog(`Response Data: ${JSON.stringify(errorData.data, null, 2)}`);
      } catch (e) {
        addLog(`Error parsing saved error: ${e.message}`);
        addLog(`Raw error data: ${lastError}`);
      }
    } else {
      addLog('No saved error found');
    }
  };

  const testRawEmployeeUpdate = async () => {
    setLoading(true);
    setTestResults('');
    
    try {
      addLog('=== RAW EMPLOYEE UPDATE TEST ===');
      
      const token = localStorage.getItem('token');
      addLog(`Token: ${token ? token.substring(0, 50) + '...' : 'No token'}`);
      
      // Test with direct fetch instead of axios
      const response = await fetch('http://localhost:5000/api/employees/2aea10fc-5fb1-11f0-a134-cbe92f2c5216', {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: 'Test Admin',
          email: 'admin@example.com',
          phone: '1234567890',
          role: 'admin',
          is_active: true
        })
      });
      
      addLog(`Response status: ${response.status}`);
      addLog(`Response headers: ${JSON.stringify(Object.fromEntries(response.headers), null, 2)}`);
      
      if (response.ok) {
        const data = await response.json();
        addLog(`Success! Response: ${JSON.stringify(data, null, 2)}`);
      } else {
        const errorData = await response.text();
        addLog(`Error response: ${errorData}`);
      }
      
    } catch (error) {
      addLog(`Raw fetch error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <h2 className="text-2xl font-bold mb-4">API Debug Tool</h2>
      
      <div className="flex gap-4 mb-6 flex-wrap">
        <button
          onClick={testAuthentication}
          disabled={loading}
          className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:opacity-50"
        >
          Test Authentication
        </button>
        
        <button
          onClick={testBackendConnection}
          disabled={loading}
          className="bg-purple-600 text-white px-4 py-2 rounded hover:bg-purple-700 disabled:opacity-50"
        >
          Test Backend Connection
        </button>
        
        <button
          onClick={testEmployeesFetch}
          disabled={loading}
          className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 disabled:opacity-50"
        >
          Test Employees Fetch
        </button>
        
        <button
          onClick={testEmployeeUpdate}
          disabled={loading}
          className="bg-orange-600 text-white px-4 py-2 rounded hover:bg-orange-700 disabled:opacity-50"
        >
          Test Employee Update
        </button>
        
        <button
          onClick={showLastError}
          disabled={loading}
          className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 disabled:opacity-50"
        >
          Show Last Error
        </button>
        
        <button
          onClick={testRawEmployeeUpdate}
          disabled={loading}
          className="bg-yellow-600 text-white px-4 py-2 rounded hover:bg-yellow-700 disabled:opacity-50"
        >
          Test Raw Update
        </button>
        
        <button
          onClick={() => setTestResults('')}
          className="bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700"
        >
          Clear
        </button>
      </div>
      
      {loading && (
        <div className="mb-4">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      )}
      
      <div className="bg-gray-100 p-4 rounded">
        <h3 className="font-semibold mb-2">Test Results:</h3>
        <pre className="whitespace-pre-wrap font-mono text-sm bg-black text-green-400 p-4 rounded overflow-auto max-h-96">
          {testResults || 'No tests run yet. Click a button above to run tests.'}
        </pre>
      </div>
    </div>
  );
};

export default DebugAPI;